
import React, { useState, useEffect } from 'react';
import Button from '../../../components/base/Button';

interface MenuItem {
  id: string;
  name: string;
  category: 'makanan' | 'minuman';
  subCategory: string;
  price: number;
  description: string;
  image: string;
  ingredients: string[];
  isAvailable: boolean;
  isPopular: boolean;
  cookingTime: number;
  calories: number;
  spiceLevel: 0 | 1 | 2 | 3;
  allergens: string[];
  createdAt: string;
  updatedAt: string;
}

export default function MenuManagement() {
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [filter, setFilter] = useState<'all' | 'makanan' | 'minuman'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');

  // Form state
  const [formData, setFormData] = useState<Partial<MenuItem>>({
    name: '',
    category: 'makanan',
    subCategory: '',
    price: 0,
    description: '',
    image: '',
    ingredients: [],
    isAvailable: true,
    isPopular: false,
    cookingTime: 15,
    calories: 0,
    spiceLevel: 0,
    allergens: []
  });

  // Mock data untuk demo
  useEffect(() => {
    const mockMenu: MenuItem[] = [
      {
        id: '1',
        name: 'Tonkotsu Ramen Deluxe',
        category: 'makanan',
        subCategory: 'Ramen',
        price: 75000,
        description: 'Ramen tradisional dengan kuah tonkotsu yang kaya, chashu premium, dan toping lengkap',
        image: 'https://public.readdy.ai/ai/img_res/c974073000378a3d201a3205d9f993db.jpg',
        ingredients: ['Mie Ramen', 'Kuah Tonkotsu', 'Chashu', 'Telur', 'Nori', 'Daun Bawang'],
        isAvailable: true,
        isPopular: true,
        cookingTime: 15,
        calories: 650,
        spiceLevel: 1,
        allergens: ['Gluten', 'Telur', 'Kedelai'],
        createdAt: '2024-01-01',
        updatedAt: '2024-01-15'
      },
      {
        id: '2',
        name: 'Omakase Sushi Set',
        category: 'makanan',
        subCategory: 'Sushi',
        price: 150000,
        description: 'Set sushi premium pilihan chef dengan ikan segar dan nasi sushi berkualitas tinggi',
        image: 'https://readdy.ai/api/search-image?query=premium%20omakase%20sushi%20set%20with%20fresh%20salmon%2C%20tuna%2C%20shrimp%2C%20eel%20nigiri%20on%20wooden%20board%2C%20wasabi%2C%20pickled%20ginger%2C%20soy%20sauce%2C%20elegant%20Japanese%20presentation%2C%20professional%20food%20photography&width=400&height=300&seq=sushi1&orientation=landscape',
        ingredients: ['Nasi Sushi', 'Salmon', 'Tuna', 'Udang', 'Unagi', 'Wasabi'],
        isAvailable: true,
        isPopular: true,
        cookingTime: 20,
        calories: 580,
        spiceLevel: 0,
        allergens: ['Ikan', 'Udang', 'Kedelai'],
        createdAt: '2024-01-01',
        updatedAt: '2024-01-14'
      },
      {
        id: '3',
        name: 'Takoyaki Osaka Style',
        category: 'makanan',
        subCategory: 'Appetizer',
        price: 45000,
        description: 'Bola takoyaki autentik ala Osaka dengan gurita segar dan saus khas',
        image: 'https://readdy.ai/api/search-image?query=authentic%20Osaka%20style%20takoyaki%20balls%20with%20octopus%2C%20topped%20with%20takoyaki%20sauce%2C%20mayonnaise%2C%20bonito%20flakes%2C%20aonori%20seaweed%20powder%2C%20served%20on%20traditional%20wooden%20boat%20plate%2C%20Japanese%20street%20food%20style&width=400&height=300&seq=takoyaki1&orientation=landscape',
        ingredients: ['Adonan Takoyaki', 'Gurita', 'Saus Takoyaki', 'Mayo', 'Katsuobushi'],
        isAvailable: true,
        isPopular: false,
        cookingTime: 12,
        calories: 320,
        spiceLevel: 0,
        allergens: ['Gluten', 'Telur', 'Seafood'],
        createdAt: '2024-01-01',
        updatedAt: '2024-01-13'
      },
      {
        id: '4',
        name: 'Matcha Latte Premium',
        category: 'minuman',
        subCategory: 'Hot Drinks',
        price: 35000,
        description: 'Minuman matcha premium dengan bubuk teh hijau pilihan dan susu berkualitas tinggi',
        image: 'https://readdy.ai/api/search-image?query=premium%20matcha%20latte%20in%20elegant%20ceramic%20cup%2C%20vibrant%20green%20color%2C%20creamy%20foam%20art%2C%20matcha%20powder%20dusting%2C%20wooden%20tray%2C%20zen%20Japanese%20ambiance%2C%20natural%20lighting%2C%20professional%20beverage%20photography&width=400&height=300&seq=matcha1&orientation=landscape',
        ingredients: ['Bubuk Matcha', 'Susu Fresh', 'Gula', 'Whipped Cream'],
        isAvailable: true,
        isPopular: true,
        cookingTime: 5,
        calories: 180,
        spiceLevel: 0,
        allergens: ['Susu'],
        createdAt: '2024-01-01',
        updatedAt: '2024-01-12'
      },
      {
        id: '5',
        name: 'Miso Ramen Futuristik',
        category: 'makanan',
        subCategory: 'Ramen',
        price: 65000,
        description: 'Inovasi ramen miso dengan sentuhan modern dan presentasi futuristik',
        image: 'https://readdy.ai/api/search-image?query=modern%20futuristic%20miso%20ramen%20bowl%20with%20rich%20miso%20broth%2C%20perfectly%20arranged%20toppings%2C%20corn%2C%20green%20onions%2C%20bamboo%20shoots%2C%20soft%20egg%2C%20artistic%20plating%2C%20contemporary%20Japanese%20restaurant%20setting&width=400&height=300&seq=misoramen1&orientation=landscape',
        ingredients: ['Mie Ramen', 'Kuah Miso', 'Jagung', 'Bambu', 'Telur', 'Daun Bawang'],
        isAvailable: false,
        isPopular: false,
        cookingTime: 18,
        calories: 580,
        spiceLevel: 2,
        allergens: ['Gluten', 'Kedelai', 'Telur'],
        createdAt: '2024-01-05',
        updatedAt: '2024-01-15'
      }
    ];
    setMenuItems(mockMenu);
  }, []);

  const filteredItems = menuItems.filter(item => {
    const matchesFilter = filter === 'all' || item.category === filter;
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.subCategory.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  // Handle image file upload
  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Harap pilih file gambar yang valid');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Ukuran file terlalu besar. Maksimal 5MB');
        return;
      }

      setImageFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setImagePreview(result);
        setFormData(prev => ({ ...prev, image: result }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Clear image
  const clearImage = () => {
    setImageFile(null);
    setImagePreview('');
    setFormData(prev => ({ ...prev, image: '' }));
    const fileInput = document.getElementById('imageUpload') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  const handleSave = () => {
    if (isEditing && selectedItem) {
      // Update existing item
      setMenuItems(prev => prev.map(item => 
        item.id === selectedItem.id 
          ? { ...item, ...formData, updatedAt: new Date().toISOString() }
          : item
      ));
    } else if (isAdding) {
      // Add new item
      const newItem: MenuItem = {
        ...formData,
        id: Date.now().toString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      } as MenuItem;
      setMenuItems(prev => [...prev, newItem]);
    }

    // Reset form
    setSelectedItem(null);
    setIsEditing(false);
    setIsAdding(false);
    setImageFile(null);
    setImagePreview('');
    setFormData({
      name: '',
      category: 'makanan',
      subCategory: '',
      price: 0,
      description: '',
      image: '',
      ingredients: [],
      isAvailable: true,
      isPopular: false,
      cookingTime: 15,
      calories: 0,
      spiceLevel: 0,
      allergens: []
    });
  };

  const handleDelete = (id: string) => {
    if (confirm('Yakin ingin menghapus menu item ini?')) {
      setMenuItems(prev => prev.filter(item => item.id !== id));
    }
  };

  const toggleAvailability = (id: string) => {
    setMenuItems(prev => prev.map(item => 
      item.id === id 
        ? { ...item, isAvailable: !item.isAvailable, updatedAt: new Date().toISOString() }
        : item
    ));
  };

  const getSpiceLevelText = (level: number) => {
    switch (level) {
      case 0: return 'Tidak Pedas';
      case 1: return 'Ringan';
      case 2: return 'Sedang';
      case 3: return 'Pedas';
      default: return 'Tidak Pedas';
    }
  };

  const subCategories = {
    makanan: ['Ramen', 'Sushi', 'Appetizer', 'Main Course', 'Dessert'],
    minuman: ['Hot Drinks', 'Cold Drinks', 'Traditional', 'Alcohol']
  };

  return (
    <div className="space-y-6">
      {/* Header & Controls */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white mb-2">Manajemen Menu</h2>
          <p className="text-gray-400">Kelola menu makanan dan minuman TAKEYA Restaurant</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 w-full lg:w-auto">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 flex items-center justify-center"></i>
            <input
              type="text"
              placeholder="Cari menu..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 bg-gray-800 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:border-red-500 focus:outline-none w-full sm:w-64 text-sm"
            />
          </div>
          <Button
            variant="primary"
            size="md"
            onClick={() => {
              setIsAdding(true);
              setSelectedItem(null);
            }}
            className="whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line mr-2 w-5 h-5 flex items-center justify-center"></i>
            Tambah Menu
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        {[
          { id: 'all', label: 'Semua Menu', count: menuItems.length },
          { id: 'makanan', label: 'Makanan', count: menuItems.filter(i => i.category === 'makanan').length },
          { id: 'minuman', label: 'Minuman', count: menuItems.filter(i => i.category === 'minuman').length }
        ].map((filterItem) => (
          <button
            key={filterItem.id}
            onClick={() => setFilter(filterItem.id as any)}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
              filter === filterItem.id
                ? 'bg-red-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {filterItem.label} ({filterItem.count})
          </button>
        ))}
      </div>

      {/* Menu Grid */}
      <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredItems.map((item) => (
          <div
            key={item.id}
            className={`bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-2xl border transition-all duration-300 overflow-hidden ${
              !item.isAvailable ? 'border-red-500/50 opacity-75' : 'border-gray-600/50 hover:border-red-500/50'
            }`}
          >
            {/* Image */}
            <div className="relative h-48 overflow-hidden">
              <img
                src={item.image}
                alt={item.name}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                {item.isPopular && (
                  <span className="bg-yellow-500 text-black px-2 py-1 rounded-full text-xs font-bold">
                    ⭐ POPULER
                  </span>
                )}
                {!item.isAvailable && (
                  <span className="bg-red-500 text-white px-2 py-1 rounded-full text-xs font-bold">
                    TIDAK TERSEDIA
                  </span>
                )}
                <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                  item.category === 'makanan' ? 'bg-red-500/80 text-white' : 'bg-blue-500/80 text-white'
                }`}>
                  {item.category === 'makanan' ? '🍜' : '🥤'} {item.subCategory}
                </span>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-white mb-1">{item.name}</h3>
                  <p className="text-gray-400 text-sm line-clamp-2">{item.description}</p>
                </div>
                <div className="text-right ml-4">
                  <div className="text-xl font-bold text-yellow-400">
                    Rp {item.price.toLocaleString('id-ID')}
                  </div>
                </div>
              </div>

              {/* Details */}
              <div className="space-y-3 mb-6">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-400">Waktu Masak:</span>
                    <div className="text-white font-semibold">{item.cookingTime} menit</div>
                  </div>
                  <div>
                    <span className="text-gray-400">Kalori:</span>
                    <div className="text-white font-semibold">{item.calories} cal</div>
                  </div>
                </div>

                {item.spiceLevel > 0 && (
                  <div className="text-sm">
                    <span className="text-gray-400">Level Pedas:</span>
                    <div className="flex items-center space-x-2">
                      <span className="text-orange-400 font-semibold">{getSpiceLevelText(item.spiceLevel)}</span>
                      <div className="flex space-x-1">
                        {Array.from({ length: 3 }, (_, i) => (
                          <div
                            key={i}
                            className={`w-2 h-2 rounded-full ${
                              i < item.spiceLevel ? 'bg-red-500' : 'bg-gray-600'
                            }`}
                          ></div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {item.allergens.length > 0 && (
                  <div className="text-sm">
                    <span className="text-gray-400">Alergen:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {item.allergens.map((allergen, index) => (
                        <span key={index} className="bg-orange-500/20 text-orange-400 px-2 py-1 rounded-full text-xs">
                          {allergen}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => {
                      setSelectedItem(item);
                      setFormData(item);
                      setIsEditing(true);
                    }}
                    className="whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-edit-line mr-2 w-4 h-4 flex items-center justify-center"></i>
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => toggleAvailability(item.id)}
                    className={`whitespace-nowrap cursor-pointer ${
                      item.isAvailable ? 'text-red-400 border-red-400' : 'text-green-400 border-green-400'
                    }`}
                  >
                    <i className={`${item.isAvailable ? 'ri-pause-line' : 'ri-play-line'} mr-2 w-4 h-4 flex items-center justify-center`}></i>
                    {item.isAvailable ? 'Nonaktifkan' : 'Aktifkan'}
                  </Button>
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDelete(item.id)}
                  className="w-full text-red-400 border-red-400 hover:bg-red-500/20 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-delete-bin-line mr-2 w-4 h-4 flex items-center justify-center"></i>
                  Hapus Menu
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredItems.length === 0 && (
        <div className="text-center py-16">
          <div className="w-20 h-20 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-restaurant-line text-gray-400 text-3xl w-10 h-10 flex items-center justify-center"></i>
          </div>
          <p className="text-gray-400 text-lg">Tidak ada menu yang ditemukan</p>
        </div>
      )}

      {/* Add/Edit Modal */}
      {(isAdding || isEditing) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl border border-red-500/20 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-white">
                  {isAdding ? 'Tambah Menu Baru' : `Edit Menu: ${selectedItem?.name}`}
                </h3>
                <button
                  onClick={() => {
                    setIsAdding(false);
                    setIsEditing(false);
                    setSelectedItem(null);
                    setImageFile(null);
                    setImagePreview('');
                    setFormData({
                      name: '',
                      category: 'makanan',
                      subCategory: '',
                      price: 0,
                      description: '',
                      image: '',
                      ingredients: [],
                      isAvailable: true,
                      isPopular: false,
                      cookingTime: 15,
                      calories: 0,
                      spiceLevel: 0,
                      allergens: []
                    });
                  }}
                  className="w-10 h-10 bg-gray-700 hover:bg-gray-600 rounded-full flex items-center justify-center transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-white w-5 h-5 flex items-center justify-center"></i>
                </button>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                {/* Left Column */}
                <div className="space-y-6">
                  <div>
                    <label className="block text-white font-semibold mb-2">Nama Menu *</label>
                    <input
                      type="text"
                      value={formData.name || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Masukkan nama menu"
                      className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:border-red-500 focus:outline-none text-sm"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white font-semibold mb-2">Kategori *</label>
                      <select
                        value={formData.category || 'makanan'}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          category: e.target.value as 'makanan' | 'minuman',
                          subCategory: '' 
                        }))}
                        className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white focus:border-red-500 focus:outline-none text-sm pr-8"
                      >
                        <option value="makanan">Makanan</option>
                        <option value="minuman">Minuman</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-white font-semibold mb-2">Sub Kategori *</label>
                      <select
                        value={formData.subCategory || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, subCategory: e.target.value }))}
                        className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white focus:border-red-500 focus:outline-none text-sm pr-8"
                      >
                        <option value="">Pilih Sub Kategori</option>
                        {subCategories[formData.category || 'makanan'].map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-white font-semibold mb-2">Harga (Rp) *</label>
                    <input
                      type="number"
                      value={formData.price || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, price: parseInt(e.target.value) || 0 }))}
                      placeholder="50000"
                      className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:border-red-500 focus:outline-none text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-white font-semibold mb-2">Deskripsi *</label>
                    <textarea
                      value={formData.description || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Deskripsi menu..."
                      rows={4}
                      className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:border-red-500 focus:outline-none text-sm resize-none"
                    />
                  </div>

                  {/* Image Upload Section */}
                  <div>
                    <label className="block text-white font-semibold mb-2">Gambar Menu</label>
                    
                    {/* Image Preview or Upload Area */}
                    <div className="relative">
                      {imagePreview || formData.image ? (
                        <div className="relative">
                          <img
                            src={imagePreview || formData.image}
                            alt="Preview menu"
                            className="w-full h-48 object-cover rounded-xl border border-gray-600"
                          />
                          <button
                            type="button"
                            onClick={clearImage}
                            className="absolute top-2 right-2 w-8 h-8 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center transition-colors cursor-pointer"
                          >
                            <i className="ri-close-line text-white w-4 h-4 flex items-center justify-center"></i>
                          </button>
                        </div>
                      ) : (
                        <label className="block w-full h-48 border-2 border-dashed border-gray-600 rounded-xl cursor-pointer hover:border-red-500 transition-colors">
                          <div className="flex flex-col items-center justify-center h-full text-gray-400 hover:text-red-400 transition-colors">
                            <i className="ri-image-line text-4xl mb-3 w-12 h-12 flex items-center justify-center"></i>
                            <p className="text-sm font-semibold mb-1">Klik untuk upload gambar</p>
                            <p className="text-xs">atau drag &amp; drop file di sini</p>
                            <p className="text-xs mt-2">Format: JPG, PNG, WebP (Max 5MB)</p>
                          </div>
                          <input
                            id="imageUpload"
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                            className="hidden"
                          />
                        </label>
                      )}
                    </div>

                    {/* Alternative: URL Input */}
                    <div className="mt-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <div className="h-px bg-gray-600 flex-1"></div>
                        <span className="text-gray-400 text-xs">ATAU</span>
                        <div className="h-px bg-gray-600 flex-1"></div>
                      </div>
                      <input
                        type="url"
                        value={!imagePreview ? (formData.image || '') : ''}
                        onChange={(e) => {
                          if (!imagePreview) {
                            setFormData(prev => ({ ...prev, image: e.target.value }));
                          }
                        }}
                        placeholder="Masukkan URL gambar..."
                        disabled={!!imagePreview}
                        className="w-full px-4 py-2 bg-gray-800/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:border-red-500 focus:outline-none text-sm disabled:opacity-50"
                      />
                    </div>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white font-semibold mb-2">Waktu Masak (menit)</label>
                      <input
                        type="number"
                        value={formData.cookingTime || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, cookingTime: parseInt(e.target.value) || 0 }))}
                        placeholder="15"
                        className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:border-red-500 focus:outline-none text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-white font-semibold mb-2">Kalori</label>
                      <input
                        type="number"
                        value={formData.calories || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, calories: parseInt(e.target.value) || 0 }))}
                        placeholder="500"
                        className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:border-red-500 focus:outline-none text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-white font-semibold mb-2">Level Pedas</label>
                    <select
                      value={formData.spiceLevel || 0}
                      onChange={(e) => setFormData(prev => ({ ...prev, spiceLevel: parseInt(e.target.value) as 0 | 1 | 2 | 3 }))}
                      className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white focus:border-red-500 focus:outline-none text-sm pr-8"
                    >
                      <option value={0}>Tidak Pedas</option>
                      <option value={1}>Ringan</option>
                      <option value={2}>Sedang</option>
                      <option value={3}>Pedas</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-white font-semibold mb-2">Bahan Utama (pisahkan dengan koma)</label>
                    <input
                      type="text"
                      value={formData.ingredients?.join(', ') || ''}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        ingredients: e.target.value.split(',').map(s => s.trim()).filter(s => s) 
                      }))}
                      placeholder="Mie Ramen, Kuah Tonkotsu, Chashu"
                      className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:border-red-500 focus:outline-none text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-white font-semibold mb-2">Alergen (pisahkan dengan koma)</label>
                    <input
                      type="text"
                      value={formData.allergens?.join(', ') || ''}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        allergens: e.target.value.split(',').map(s => s.trim()).filter(s => s) 
                      }))}
                      placeholder="Gluten, Telur, Kedelai"
                      className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:border-red-500 focus:outline-none text-sm"
                    />
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="isAvailable"
                        checked={formData.isAvailable || false}
                        onChange={(e) => setFormData(prev => ({ ...prev, isAvailable: e.target.checked }))}
                        className="w-5 h-5 bg-gray-800 border border-gray-600 rounded text-red-500 focus:ring-red-500"
                      />
                      <label htmlFor="isAvailable" className="text-white font-semibold">Menu Tersedia</label>
                    </div>

                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="isPopular"
                        checked={formData.isPopular || false}
                        onChange={(e) => setFormData(prev => ({ ...prev, isPopular: e.target.checked }))}
                        className="w-5 h-5 bg-gray-800 border border-gray-600 rounded text-red-500 focus:ring-red-500"
                      />
                      <label htmlFor="isPopular" className="text-white font-semibold">Menu Populer</label>
                    </div>
                  </div>

                  {/* Image Upload Tips */}
                  <div className="bg-blue-900/20 rounded-xl p-4 border border-blue-500/20">
                    <h4 className="text-blue-400 font-semibold mb-2 flex items-center">
                      <i className="ri-information-line mr-2 w-4 h-4 flex items-center justify-center"></i>
                      Tips Upload Gambar
                    </h4>
                    <ul className="text-blue-300 text-sm space-y-1">
                      <li>• Gunakan foto berkualitas tinggi</li>
                      <li>• Rasio ideal: 4:3 atau 16:9</li>
                      <li>• Background bersih &amp; pencahayaan baik</li>
                      <li>• Fokus pada makanan sebagai objek utama</li>
                      <li>• Format: JPG, PNG, atau WebP</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex gap-4 mt-8">
                <Button
                  variant="primary"
                  size="lg"
                  onClick={handleSave}
                  disabled={!formData.name || !formData.subCategory || !formData.price}
                  className="flex-1 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-check-line mr-2 w-5 h-5 flex items-center justify-center"></i>
                  {isAdding ? 'Tambah Menu' : 'Simpan Perubahan'}
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => {
                    setIsAdding(false);
                    setIsEditing(false);
                    setSelectedItem(null);
                    setImageFile(null);
                    setImagePreview('');
                  }}
                  className="whitespace-nowrap cursor-pointer"
                >
                  Batal
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
